import React, { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'

interface AudioFileActionsProps {
  nodeId: string
  audioUrl: string
  filename?: string
  /** Generated child nodes keep their file: no replace or remove. */
  hasParent: boolean
  onReplace: () => void
  onRemove: () => void
}

const BUTTON_CLASS = 'h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent'

/** Hover actions on a loaded AudioFile node: download, replace, remove. */
export function AudioFileActions({ nodeId, audioUrl, filename, hasParent, onReplace, onRemove }: AudioFileActionsProps) {
  const handleDownload = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const response = await fetch(audioUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || `audio-${nodeId}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download audio:', err)
    }
  }, [audioUrl, filename, nodeId])

  const handleReplace = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onReplace()
  }, [onReplace])

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onRemove()
  }, [onRemove])

  return (
    <div className="absolute top-2 right-2 flex gap-1">
      <Button variant="ghost" size="sm" className={BUTTON_CLASS} onClick={handleDownload} title="Download audio" aria-label="Download audio">
        <Icon name="download" className="h-4 w-4" />
      </Button>
      {!hasParent && (
        <>
          <Button variant="ghost" size="sm" className={BUTTON_CLASS} onClick={handleReplace} title="Replace audio" aria-label="Replace audio">
            <Icon name="upload" className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className={BUTTON_CLASS} onClick={handleRemove} title="Remove audio" aria-label="Remove audio">
            <Icon name="x" className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  )
}
